import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@material-ui/core";
import {
  CustomAsyncAutocomplete,
  CustomAutocomplete,
  CustomDateRangePicker,
  CustomInput,
  CustomSelect,
  CustomSwitch,
  PhoneNumberInput,
} from "../../../../../Components";
import { formatDisplayDate } from "../../../SmartProposal/Helper/formatDate";
import { GetAfterSalesAgents, lookupItemsGetId } from "../../../../../Services";
import { ContactClassTypesEnum, NationalityEnum } from "../../../../../Enums";
import { useDebouncedAction } from "../../../../../Hooks/DebouncedAction";
import { useSelectedTheme, useTranslate } from "../../../../../Hooks";

// Icons
import { UserOneIcon } from "../../../../../assets/icons";

// styles
import useStyles from "./styles";

function FiltersSection({ filters, setFilters }) {
  const styles = useStyles();
  const { translate } = useTranslate("AfterSalesView");
  const {
    theme: { palette },
  } = useSelectedTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const dateTypeOptions = [
    { key: 1, value: translate("created-date"), type: "createdOn" },
    { key: 2, value: "Handover Date", type: "handoverDate" },
    { key: 3, value: "Date of Birth", type: "dateOfBirth" },
  ];
  const internationalOptions = [
    { key: 1, value: translate("Yes"), type: "yes" },
    { key: 0, value: translate("No"), type: "no" },
  ];
  const [selectedDateType, setSelectedDateType] = useState(
    dateTypeOptions[0].type
  );
  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
    key: "selection",
  });
  const [dateOfBirth, setDateOfBirth] = useState({
    day: null,
    month: null,
  });

  const GetCountries = useCallback(async () => {
    try {
      setIsLoading(true);
      const res = await lookupItemsGetId({
        lookupTypeId: NationalityEnum.lookupTypeId,
      });

      if (!(res && res.status && res.status !== 200)) {
        setCountryData(res);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
      setCountryData([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const debouncedSetPhone = useDebouncedAction(
    (value) => setFilters((prev) => ({ ...prev, clientMobile: value })),
    { delay: 2000 }
  );

  const contactClassOptions = Object.values(ContactClassTypesEnum);

  useEffect(() => {
    GetCountries();
  }, []);

  return (
    <Box className={styles.filtersSection}>
      <Box className={styles.leftSection}>
        <Box className={styles.selectFields}>
          <CustomSelect
            options={contactClassOptions}
            placeholder={translate("contact-class")}
            value={
              contactClassOptions.find(
                (option) => option.name === filters.clientContactClass
              )?.key || null
            }
            onValueChange={(selectedKey) => {
              const selectedOption = contactClassOptions.find(
                (option) => option.key === selectedKey
              );
              setFilters((prev) => ({
                ...prev,
                clientContactClass: selectedOption ? selectedOption.name : null,
              }));
            }}
            variant="default"
            disabled={isLoading}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.key}
            isClearable={true}
            customSelectClasses={styles.customSelect}
          />
          <CustomAutocomplete
            placeholder={translate("nationality")}
            customWrapperAutocomplete={styles.customAutocomplete}
            options={countryData}
            isDisabled={isLoading}
            value={
              countryData?.find(
                (item) => item.lookupItemName === filters.clientNationality
              ) || null
            }
            onChange={(e, value) => {
              setFilters((prev) => ({
                ...prev,
                clientNationality: value ? value.lookupItemName : null,
              }));
            }}
            getOptionLabel={(option) => option?.lookupItemName || null}
            getOptionValue={(option) => option?.lookupItemName || null}
            renderOption={(option) => option?.lookupItemName || ""}
            enableTextTruncation
          />
          {/************************************** This still is not finished yet *****************************************/}
          <CustomAsyncAutocomplete
            rootCustomClasses={styles.inputwrapper}
            className={styles.customAutocomplete}
            placeholder={translate("agent-name")}
            onFetchData={GetAfterSalesAgents}
            filter={{ pageIndex: 0, pageSize: 1000 }}
            selectedValues={filters.assignedAgent || null}
            displayLabel={(option) => option?.fullName ?? ""}
            renderOption={(option) => option?.fullName}
            chipsLabel={(option) => {
              return option?.fullName;
            }}
            onChange={(_e, value) => {
              setFilters((prev) => ({
                ...prev,
                assignedAgent: value,
              }));
            }}
            getOptionSelected={(option, value) =>
              option?.userId === value?.userId
            }
            mapper={(data) => data}
          />
          <CustomInput
            type="text"
            placeholder="mail@mail.com"
            hasSearchIcon={false}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, clientEmail: e.target.value }))
            }
            debounceTime={1000}
            disabled={isLoading}
            value={filters.clientEmail}
            inputContainerOverrideStyles={styles.customInputContainer}
          />
          <CustomInput
            type="text"
            placeholder={translate("client-name")}
            hasSearchIcon={false}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, clientName: e.target.value }))
            }
            debounceTime={1000}
            disabled={isLoading}
            value={filters.clientName}
            inputContainerOverrideStyles={styles.customInputContainer}
          />
          <CustomInput
            type="number"
            placeholder={translate("client ID")}
            hasSearchIcon={false}
            onChange={(e) => {
              if (e.target?.value === "") {
                return setFilters((prev) => ({ ...prev, ContactId: " " }));
              }
              setFilters((prev) => ({ ...prev, ContactId: e.target?.value }));
            }}
            disabled={isLoading}
            inputContainerOverrideStyles={styles.customInputContainer}
            value={filters.ContactId ?? " "}
            debounceTime={700}
          />
        </Box>
        <PhoneNumberInput
          name="phoneNumber-ref"
          idRef="phoneNumberRef"
          inputPlaceholder="+1 (555) 000-0000"
          value={filters.clientMobile || ""}
          country="ae"
          onInputChanged={debouncedSetPhone}
          wrapperClasses={styles.phoneInputWrapper}
        />
        <Box style={{ maxWidth: "198px" }}>
          <CustomSelect
            width={"198px"}
            options={internationalOptions}
            onValueChange={(selectedKey) => {
              const selectedOption =
                selectedKey?.toLowerCase() === "yes" ? true : false;
              setFilters((prev) => ({
                ...prev,
                IsInternational: selectedOption,
              }));
            }}
            variant="default"
            disabled={isLoading}
            getOptionLabel={(option) => option.value}
            getOptionValue={(option) => option.value}
            placeholder={translate("international clients")}
            isClearable
          />
        </Box>
        <CustomSwitch
          size="md"
          label="Not Assigned Clients"
          onChange={(e) => {
            const isChecked = e.target.checked;
            setFilters((prev) => ({
              ...prev,
              IsNotAssigned: isChecked ? true : null,
            }));
          }}
        />
      </Box>
      <Box
        className={styles.rightSection}
        style={{ justifyContent: "flex-end" }}
      >
        <Box style={{ maxWidth: "165px" }}>
          <CustomSelect
            width={"165px"}
            options={dateTypeOptions}
            value={
              dateTypeOptions.find((opt) => opt.type === selectedDateType)?.key
            }
            onValueChange={(selectedKey) => {
              const selectedOption = dateTypeOptions.find(
                (opt) => opt.key === selectedKey
              );
              setSelectedDateType(selectedOption.type);
              setDateRange({
                startDate: null,
                endDate: null,
                key: "selection",
              });
              setDateOfBirth({
                day: null,
                month: null,
              });
              // Clear date of birth filters when switching away
              setFilters((prev) => {
                const updated = { ...prev };
                delete updated.DayOfBirth;
                delete updated.MonthOfBirth;
                return updated;
              });
            }}
            variant="default"
            disabled={isLoading}
            getOptionLabel={(option) => option.value}
            getOptionValue={(option) => option.key}
            defaultValue={dateTypeOptions[0].key}
          />
        </Box>

        {selectedDateType === "dateOfBirth" ? (
          <>
            <Box style={{ maxWidth: "100px" }}>
              <CustomSelect
                width={"100px"}
                options={Array.from({ length: 31 }, (_, i) => ({
                  key: i + 1,
                  value: i + 1,
                }))}
                value={dateOfBirth.day}
                onValueChange={(selectedKey) => {
                  setDateOfBirth((prev) => ({
                    ...prev,
                    day: selectedKey ? selectedKey : null,
                  }));
                  setFilters((prev) => ({
                    ...prev,
                    DayOfBirth: selectedKey ? selectedKey : null,
                  }));
                }}
                variant="default"
                disabled={isLoading}
                getOptionLabel={(option) => option.value.toString()}
                getOptionValue={(option) => option.key}
                placeholder="Day"
                isClearable
              />
            </Box>
            <Box style={{ maxWidth: "100px" }}>
              <CustomSelect
                width={"100px"}
                options={Array.from({ length: 12 }, (_, i) => ({
                  key: i + 1,
                  value: i + 1,
                }))}
                value={dateOfBirth.month}
                onValueChange={(selectedKey) => {
                  setDateOfBirth((prev) => ({
                    ...prev,
                    month: selectedKey ? selectedKey : null,
                  }));
                  setFilters((prev) => ({
                    ...prev,
                    MonthOfBirth: selectedKey ? selectedKey : null,
                  }));
                }}
                variant="default"
                disabled={isLoading}
                getOptionLabel={(option) => option.value.toString()}
                getOptionValue={(option) => option.key}
                placeholder="Month"
                isClearable
              />
            </Box>
          </>
        ) : (
          <CustomDateRangePicker
            ranges={[dateRange]}
            dateRangesClassName={styles.dateRanges}
            onDateChanged={(selectedDate) => {
              setDateRange((prev) => ({
                ...prev,
                startDate: selectedDate.selection?.startDate || null,
                endDate: selectedDate.selection?.endDate || null,
              }));
            }}
            onSave={() => {
              setFilters((prev) => {
                const updated = { ...prev };
                updated.fromCreatedOn = null;
                updated.toCreatedOn = null;
                updated.FromHandoverDate = null;
                updated.ToHandoverDate = null;
                const formatDate = (date) =>
                  formatDisplayDate(date, "YYYY-MM-DD");
                if (selectedDateType === "createdOn") {
                  updated.fromCreatedOn = formatDate(dateRange.startDate);
                  updated.toCreatedOn = formatDate(dateRange.endDate);
                } else {
                  updated.FromHandoverDate = formatDate(dateRange.startDate);
                  updated.ToHandoverDate = formatDate(dateRange.endDate);
                }
                return updated;
              });
            }}
            onClearClicked={() => {
              setDateRange({
                startDate: null,
                endDate: null,
                key: "selection",
              });
              setFilters((prev) => {
                const cleared = { ...prev };
                cleared.fromCreatedOn = null;
                cleared.toCreatedOn = null;
                cleared.FromHandoverDate = null;
                cleared.ToHandoverDate = null;
                return cleared;
              });
            }}
            isDisabled={isLoading}
            emptyLabel="dd/mm/yyyy"
            displayFormat="MMM D, YYYY"
          />
        )}
      </Box>
    </Box>
  );
}

export default FiltersSection;
